import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { API_BASE_URL } from './config';
import UserRegistration from './components/UserRegistration';
import UserLogin from './components/UserLogin';
import UserLoginPassword from './components/UserLoginPassword';
import RegisterWithBaby from './components/RegisterWithBaby';
import RegisterWithInvite from './components/RegisterWithInvite';
import WelcomePage from './pages/WelcomePage';
import OnboardingChoice from './pages/OnboardingChoice';
import LoggedInApp from './components/LoggedInApp';
import Admin from './pages/Admin';
import Support from './pages/Support';
import Privacy from './pages/Privacy';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { ToastProvider } from './contexts/ToastContext';
import Toast from './components/Toast';
import LiveUpdatesDebug from './components/LiveUpdatesDebug';
import { startTracking } from './utils/openreplay';

function AppContent() {
  const [user, setUser] = useState<any>(null);
  const [authMode, setAuthMode] = useState<'register' | 'login' | 'password'>('register');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Set authMode based on route
  useEffect(() => {
    if (location.pathname === '/signin') {
      setAuthMode('login');
    } else if (location.pathname === '/auth' || location.pathname === '/start-tracking' || location.pathname === '/join-invite') {
      setAuthMode('register');
    }
  }, [location.pathname]);

  // Check for stored user on app load
  useEffect(() => {
    const verifyAndLoadUser = async () => {
      const storedUser = localStorage.getItem('babysleep_user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);

          // Verify user still exists in database
          try {
            const response = await fetch(`${API_BASE_URL}/api/users/${userData.id}/verify`);
            if (response.ok) {
              setUser(userData);
            } else {
              // User doesn't exist in database, clear localStorage
              console.log('User no longer exists in database, clearing session');
              localStorage.removeItem('babysleep_user');
            }
          } catch (error) {
            console.error('Failed to verify user:', error);
            // On network error, keep the user logged in
            setUser(userData);
          }
        } catch (error) {
          console.error('Failed to parse stored user data:', error);
          localStorage.removeItem('babysleep_user');
        }
      }
      setIsLoading(false);
    };

    verifyAndLoadUser();
  }, []);

  // Redirect to main app when user logs in/registers
  useEffect(() => {
    if (user && (location.pathname === '/auth' || location.pathname === '/start-tracking' ||
                 location.pathname === '/join-invite' || location.pathname === '/signin')) {
      navigate('/', { replace: true });
    }
  }, [user, location.pathname, navigate]);

  // Start OpenReplay tracking for all users (anonymous and authenticated)
  useEffect(() => {
    if (!isLoading) {
      console.log('[OpenReplay Debug] App useEffect - starting tracking for all users');
      startTracking();
    }
  }, [isLoading]);

  const handleAuthSuccess = (userData: any) => {
    console.log('User authenticated:', userData);
    setUser(userData);
    // Store user in localStorage
    localStorage.setItem('babysleep_user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('babysleep_user');
  };

  const handleUserUpdate = (userData: any) => {
    setUser(userData);
    localStorage.setItem('babysleep_user', JSON.stringify(userData));
  };

  // Show loading while checking localStorage
  if (isLoading) {
    return (
      <div className="App">
        <div className="app-loading">
          <h2>Baby Sleep Tracker</h2>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const AuthPage = () => (
    <div className="App">
      <header className="App-header">
        <h1>Baby Sleep Tracker</h1>
        <p>Simple sleep tracking for your little one</p>
      </header>
      <main>
        {authMode === 'register' ? (
          <div>
            <UserRegistration onRegistrationSuccess={handleAuthSuccess} />
            <div className="auth-toggle">
              <p>Already have an account?</p>
              <button
                onClick={() => setAuthMode('login')}
                className="auth-switch-btn"
              >
                Login with Email Code
              </button>
              <button
                onClick={() => setAuthMode('password')}
                className="auth-switch-btn"
                style={{ marginTop: '0.5rem' }}
              >
                Login with Password
              </button>
            </div>
          </div>
        ) : authMode === 'login' ? (
          <div>
            <UserLogin
              onLoginSuccess={handleAuthSuccess}
              onSwitchToPassword={() => setAuthMode('password')}
            />
            <div className="auth-toggle">
              <p>Don't have an account?</p>
              <button
                onClick={() => setAuthMode('register')}
                className="auth-switch-btn"
              >
                Create Account
              </button>
            </div>
          </div>
        ) : (
          <div>
            <UserLoginPassword onLoginSuccess={handleAuthSuccess} />
            <div className="auth-toggle">
              <p>Don't have an account?</p>
              <button
                onClick={() => setAuthMode('register')}
                className="auth-switch-btn"
              >
                Create Account
              </button>
              <button
                onClick={() => setAuthMode('login')}
                className="auth-switch-btn"
                style={{ marginTop: '0.5rem' }}
              >
                Login with Email Code
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );

  const MainApp = () => {
    if (user) {
      return (
        <WebSocketProvider userId={user.id}>
          <LoggedInApp user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
        </WebSocketProvider>
      );
    }
    return <Navigate to="/welcome" replace />;
  };

  return (
    <>
      <Routes>
        <Route path="/adm-page" element={<Admin />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/welcome" element={user ? <Navigate to="/" replace /> : <WelcomePage />} />
        <Route path="/get-started" element={user ? <Navigate to="/" replace /> : <OnboardingChoice onAuthSuccess={handleAuthSuccess} />} />
        <Route path="/start-tracking" element={user ? <Navigate to="/" replace /> : <RegisterWithBaby onRegistrationSuccess={handleAuthSuccess} />} />
        <Route path="/join-invite" element={user ? <Navigate to="/" replace /> : <RegisterWithInvite onRegistrationSuccess={handleAuthSuccess} />} />
        <Route path="/signin" element={user ? <Navigate to="/" replace /> : <AuthPage />} />
        <Route path="/support" element={<Support />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/*" element={<MainApp />} />
      </Routes>
      <Toast />
    </>
  );
}

function App() {
  return (
    <ToastProvider>
      <Router>
        <AppContent />
        <LiveUpdatesDebug />
      </Router>
    </ToastProvider>
  );
}

export default App;
